import { Box, InputBase, Slider } from '@mui/material'
import { memo, useCallback, useRef, useState } from 'react'
import { colorSelectorSliderSlots } from '../sliderStyles/colorSelectorSliderSlots'
import {
    COLOR_SELECTOR_SLIDER_WIDTH,
    getSliderChromeBoxSx,
    useSettingsSliderStyles,
} from '../sliderStyles/colorSelectorSlider.styles'

export type SettingsNumericSliderProps = {
    value: number
    min: number
    max: number
    step?: number
    suffix?: string
    onChange: (value: number) => void
}

const SettingsNumericSlider: React.FC<SettingsNumericSliderProps> = ({
    value,
    min,
    max,
    step = 1,
    suffix = '',
    onChange,
}) => {
    const { classes } = useSettingsSliderStyles()
    const rafRef = useRef<number>()
    const displayValue = Math.round(value)
    const [inputVal, setInputVal] = useState<string>('')
    const [editing, setEditing] = useState(false)

    const clamp = useCallback(
        (next: number) => Math.min(max, Math.max(min, next)),
        [min, max]
    )

    const emitChange = useCallback(
        (next: number) => {
            onChange(clamp(next))
        },
        [clamp, onChange]
    )

    const handleSliderChange = useCallback(
        (_event: Event, newValue: number | number[]) => {
            cancelAnimationFrame(rafRef.current!)
            rafRef.current = requestAnimationFrame(() => {
                const next = Array.isArray(newValue) ? newValue[0] : newValue
                emitChange(next)
            })
        },
        [emitChange]
    )

    const handleInputFocus = useCallback(() => {
        setEditing(true)
        setInputVal(String(displayValue))
    }, [displayValue])

    const commitInput = useCallback(
        (raw: string) => {
            setEditing(false)
            const num = parseInt(raw, 10)
            if (!isNaN(num)) {
                emitChange(num)
            }
        },
        [emitChange]
    )

    const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            ;(e.target as HTMLInputElement).blur()
        } else if (e.key === 'Escape') {
            setEditing(false)
        }
    }, [])

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                flexShrink: 0,
                width: COLOR_SELECTOR_SLIDER_WIDTH + 52,
            }}
        >
            <Box sx={getSliderChromeBoxSx()}>
                <Slider
                    size="small"
                    className={classes.root}
                    slots={colorSelectorSliderSlots}
                    value={displayValue}
                    min={min}
                    max={max}
                    step={step}
                    onChange={handleSliderChange}
                />
            </Box>
            <InputBase
                value={editing ? inputVal : displayValue}
                onFocus={handleInputFocus}
                onChange={(e) => setInputVal(e.target.value)}
                onBlur={(e) => commitInput(e.target.value)}
                onKeyDown={handleInputKeyDown}
                endAdornment={
                    suffix ? (
                        <Box
                            component="span"
                            sx={{
                                fontSize: '11px',
                                color: 'text.secondary',
                                pr: '4px',
                                pl: '2px',
                                lineHeight: 1,
                                userSelect: 'none',
                            }}
                        >
                            {suffix}
                        </Box>
                    ) : undefined
                }
                inputProps={{
                    min,
                    max,
                    step,
                }}
                sx={{
                    width: 46,
                    height: 22,
                    fontSize: '12px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '4px',
                    flexShrink: 0,
                    '& .MuiInputBase-input': {
                        textAlign: 'right',
                        padding: suffix ? 0 : '0 6px',
                        height: '100%',
                        boxSizing: 'border-box',
                    },
                }}
            />
        </Box>
    )
}

export default memo(SettingsNumericSlider)
